// @mentions in posts and replies.
//
// ⚠️ Mirrors mention_handles() in supabase/migrations/053_mentions.sql, which
// is what actually decides who gets notified — change both together. What
// renders as a mention here must be exactly what the server notifies:
//   • '@' not preceded by a letter, digit, '.', '/' or '@' — so an email
//     (me@jane.com) and a URL path (medium.com/@jane) are not mentions.
//     URLs never reach this anyway: linkify runs first and link segments
//     are left alone.
//   • then 3–30 of [A-Za-z0-9_], with trailing underscores handed back to the
//     text (a username can't end in one, and "_@jane_" is italics).
//   • lower-cased for the link; displayed as typed.
//
// A handle nobody owns still renders as a mention; its /u/ link says "no
// member by that name". The server simply notifies nobody.

const MENTION_RE = /@([A-Za-z0-9_]{3,30})(?![A-Za-z0-9_])/g;
const BLOCKS_MENTION = /[A-Za-z0-9./@]/;

export const mentionPath = (handle) => `/u/${encodeURIComponent(handle.toLowerCase())}`;

// Splits plain text (no links in it) into text + mention segments. Segments
// join back to exactly `text` — the editor's highlight layer depends on that.
export const splitMentions = (text) => {
  if (!text || !text.includes('@')) return [{ type: 'text', value: text ?? '' }];
  const out = [];
  let last = 0;
  for (const match of text.matchAll(MENTION_RE)) {
    const at = match.index;
    if (at > 0 && BLOCKS_MENTION.test(text[at - 1])) continue;
    const handle = match[1].replace(/_+$/, '');
    if (handle.length < 3) continue;
    if (at > last) out.push({ type: 'text', value: text.slice(last, at) });
    out.push({ type: 'mention', value: `@${handle}`, handle: handle.toLowerCase() });
    last = at + 1 + handle.length;
  }
  if (last < text.length) out.push({ type: 'text', value: text.slice(last) });
  return out.length ? out : [{ type: 'text', value: text }];
};

// The @word being typed at the caret, for the editor's suggestions:
// { start, query } where `start` is the index of the '@'. null when the caret
// isn't in a mention. An empty query (just typed '@') counts, so the caller
// can decide how many characters to wait for.
export const activeMention = (value, caret) => {
  const before = (value ?? '').slice(0, caret);
  const m = before.match(/(^|[^A-Za-z0-9./@])@([A-Za-z0-9_]{0,30})$/);
  if (!m) return null;
  return { start: caret - m[2].length - 1, query: m[2] };
};
